import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft, Save, Loader2, Lock, Eye, FileDown, CheckCircle2, ChevronDown, ChevronUp, Plus, AlertTriangle, X,
  HardDriveDownload, Globe, Users,
} from 'lucide-react';
import { SigeEinweisung, EinweisungGruppe, EinweisungOnlineTeilnahme } from '../sige-plan/types';
import { CompanyInfo } from '../../services/pdf/letterhead';
import { updateEinweisung, abschliessenEinweisung } from '../../services/sigeEinweisungService';
import { describeError } from '../../services/errorMessage';
import { buildEinweisungVorschlag, schaetzeSeiten } from '../../services/sigeEinweisung';
import { ladePlanQuelle } from './useEinweisungen';
import { PunkteEditor } from './PunkteEditor';
import { TeilnehmerListe } from './TeilnehmerListe';
import { EinweisungSeiten, exportiereEinweisungPdf, zaehleSeitenSicher } from './einweisungIntegration';
import {
  EinweisungEntwurf,
  EinweisungEntwurfDaten,
  SicherungsStand,
  autoSpeicherPlan,
  baueEntwurf,
  beschreibeSeiten,
  beschreibeSeitenGenau,
  beschreibeSicherung,
  beschreibeSpeicherstand,
  bereinigeGruppen,
  bereinigeTeilnehmer,
  darfEntwurfSichern,
  einweisungTitel,
  entwurfsFrage,
  formatDatum,
  fuegeGruppeHinzu,
  fuegePunktHinzu,
  pruefeEntwurf,
  onlineTeilnehmerName,
  sortiereOnlineTeilnahmen,
  uebernimmVorschlag,
  verbindeUnterschriften,
  vereinigeTeilnehmer,
  zaehleUnterschriften,
} from './einweisungLogic';
import { formatZeitpunkt } from './onlineTeilnahmenLogic';
import { leseEntwurf, raeumeEntwurf, sichereEntwurf } from './einweisungEntwurf';

const KEINE_ONLINE: EinweisungOnlineTeilnahme[] = [];

/**
 * Farbe der Zustandszeile. `warnung` ist bewusst kraeftig: sie sagt, dass der
 * Stand NICHT auf dem Server liegt — das muss auf einem hellen Display in der
 * Sonne auffallen.
 */
const TON_KLASSE: Record<'ruhig' | 'wartend' | 'warnung', string> = {
  ruhig: 'font-semibold text-emerald-700',
  wartend: 'font-semibold text-slate-500',
  warnung: 'font-bold text-amber-700',
};

interface Props {
  einweisung: SigeEinweisung;
  /**
   * Online-Teilnahmen dieser Einweisung (eigene Tabelle) — NUR zur Anzeige und
   * fuer PDF/Seitenzaehler. Sie werden nie in `teilnehmer` geschrieben.
   */
  online?: EinweisungOnlineTeilnahme[];
  projektName?: string;
  projektNummer?: string;
  bauvorhabenAdresse?: string;
  /** Firmen-Briefkopf fuer das PDF (buildCompanyForPdf im Container). */
  company: CompanyInfo;
  isReadOnly?: boolean;
  isMobile?: boolean;
  onZurueck: () => void;
  /** Nach jedem erfolgreichen Speichern — der Container haelt die Ablage aktuell. */
  onGespeichert: (e: SigeEinweisung) => void;
  /** Nach dem Abschliessen: der Container stellt die Abrechnungsfrage. */
  onAbgeschlossen: (e: SigeEinweisung) => void;
  /** Oeffnet die Teilnahmen-Ansicht (aus dem Online-Block heraus). */
  onOnlineTeilnahmen?: () => void;
}

/**
 * Der Editor einer Einweisung: Kopfdaten, Punkte, Teilnehmer, Vorschau,
 * Abschliessen.
 *
 * ─── Abgeschlossen heisst abgeschlossen ────────────────────────────────────
 * Unter dem Blatt stehen Unterschriften. Ein Textbaustein, der sich
 * nachtraeglich aendert, macht sie wertlos — der Editor sperrt deshalb
 * vollstaendig, sobald `status === 'abgeschlossen'`. Derselbe Riegel sitzt
 * ausserdem in updateEinweisung als WHERE-Bedingung; diese Sperre hier ist die
 * hoefliche Variante davon, nicht ihr Ersatz.
 *
 * ─── Nur-Ansicht ──────────────────────────────────────────────────────────
 * `isReadOnly` sperrt jeden Schreibpfad genauso: Speichern, Abschliessen,
 * Vorschlag uebernehmen, Unterschreiben. Der Export bleibt offen — er
 * schreibt nichts und bucht ausdruecklich nicht.
 *
 * ─── Online-Teilnahmen ────────────────────────────────────────────────────
 * Wer ueber den Link/QR unterschrieben hat, steht in einer EIGENEN Tabelle
 * (`online`). Der Editor zeigt diese Zeilen nur an und rechnet sie in
 * Seitenzahl, Vorschau und PDF ein (`alsDatensatzMitOnline`). Gespeichert
 * wird IMMER `alsDatensatz()` ohne sie — sonst schriebe das automatische
 * Speichern die Online-Zeilen ins jsonb, und beim naechsten Durchlauf
 * stuenden sie doppelt oder gar nicht mehr da.
 */
export const EinweisungEditor: React.FC<Props> = ({
  einweisung,
  online = KEINE_ONLINE,
  projektName,
  projektNummer,
  bauvorhabenAdresse,
  company,
  isReadOnly = false,
  isMobile = false,
  onZurueck,
  onGespeichert,
  onAbgeschlossen,
  onOnlineTeilnahmen,
}) => {
  const abgeschlossen = einweisung.status === 'abgeschlossen';
  const gesperrt = isReadOnly || abgeschlossen;

  const [datum, setDatum] = useState(einweisung.datum || '');
  const [uhrzeit, setUhrzeit] = useState(einweisung.uhrzeit || '');
  const [ort, setOrt] = useState(einweisung.ort || '');
  const [dauer, setDauer] = useState(einweisung.dauer_min == null ? '' : String(einweisung.dauer_min));
  const [durchfuehrender, setDurchfuehrender] = useState(einweisung.durchfuehrender_name || '');
  const [titel, setTitel] = useState(einweisung.titel || '');
  const [bemerkung, setBemerkung] = useState(einweisung.bemerkung || '');
  const [gruppen, setGruppen] = useState<EinweisungGruppe[]>(einweisung.punkte || []);
  const [teilnehmer, setTeilnehmer] = useState(einweisung.teilnehmer || []);
  const [leerzeilen, setLeerzeilen] = useState(einweisung.leerzeilen ?? 5);

  const [kopfOffen, setKopfOffen] = useState(!isMobile);
  const [speichert, setSpeichert] = useState(false);
  const [schliesstAb, setSchliesstAb] = useState(false);
  const [vorschlagLaeuft, setVorschlagLaeuft] = useState(false);
  const [zeigeVorschau, setZeigeVorschau] = useState(false);
  const [exportiert, setExportiert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  /**
   * Ein lokal gefundener Stand, der dem Anwender ANGEBOTEN wird. Solange er
   * offen steht, wird nichts gesichert — der Fund darf nicht von der laufenden
   * Bearbeitung ueberschrieben werden, bevor die Frage beantwortet ist.
   */
  const [angebot, setAngebot] = useState<EinweisungEntwurf | null>(null);
  /** `null` = auf diesem Geraet liegt (noch) nichts. */
  const [sicherung, setSicherung] = useState<SicherungsStand | null>(null);

  /** Gescheiterte automatische Anlaeufe seit der letzten Eingabe. */
  const [autoFehler, setAutoFehler] = useState(0);
  /** Uhrzeit des letzten automatischen Speicherns, `null` nach Knopfdruck. */
  const [zuletztAuto, setZuletztAuto] = useState<string | null>(null);

  /**
   * Riegel gegen den zweiten Fingertipp.
   *
   * Der Zustand `speichert`/`schliesstAb` steht erst NACH dem naechsten
   * Zeichnen; zwei schnelle Beruehrungen — auf der Baustelle mit Handschuh der
   * Normalfall — laufen davor beide durch. Beim Abschliessen haengt daran die
   * Leistungsbuchung, deshalb sitzt der Riegel in einer Referenz und wirkt
   * sofort.
   */
  const speichertRef = useRef(false);
  const schliesstAbRef = useRef(false);

  // Wechselt der Datensatz (andere Einweisung geoeffnet), muss der Entwurf mit.
  useEffect(() => {
    setDatum(einweisung.datum || '');
    setUhrzeit(einweisung.uhrzeit || '');
    setOrt(einweisung.ort || '');
    setDauer(einweisung.dauer_min == null ? '' : String(einweisung.dauer_min));
    setDurchfuehrender(einweisung.durchfuehrender_name || '');
    setTitel(einweisung.titel || '');
    setBemerkung(einweisung.bemerkung || '');
    setGruppen(einweisung.punkte || []);
    setTeilnehmer(einweisung.teilnehmer || []);
    setLeerzeilen(einweisung.leerzeilen ?? 5);
    setDirty(false);
    setFehler(null);
    setHinweis(null);
    setSicherung(null);
    setAutoFehler(0);
    setZuletztAuto(null);
    speichertRef.current = false;
    schliesstAbRef.current = false;

    // Liegt auf diesem Geraet ein Stand, der neuer ist als der vom Server?
    // Dann wird er ANGEBOTEN, nie stillschweigend eingespielt.
    const gefunden = pruefeEntwurf(leseEntwurf(einweisung.id), einweisung);
    setAngebot(gefunden.zustand === 'anbieten' ? gefunden.entwurf! : null);
    if (gefunden.zustand === 'veraltet') raeumeEntwurf(einweisung.id);
  }, [einweisung.id]);

  const aendern = <T,>(setter: (v: T) => void) => (wert: T) => {
    setter(wert);
    setDirty(true);
  };

  /** Der rohe Bearbeitungsstand der Felder — Grundlage der Gerätesicherung. */
  const aktuelleDaten = (): EinweisungEntwurfDaten => ({
    titel, datum, uhrzeit, ort, dauer, durchfuehrender, bemerkung,
    punkte: gruppen, teilnehmer, leerzeilen,
  });

  /**
   * ENTWURFSSICHERUNG FUERS FUNKLOCH.
   *
   * Auf der Baustelle faellt das Netz aus, und der Browser wird vom
   * Betriebssystem weggeraeumt, sobald jemand die Kamera oeffnet. Ohne diese
   * Sicherung waere die Arbeit dann weg — mitsamt Unterschriften, die man von
   * den Anwesenden nicht ein zweites Mal bekommt.
   *
   * Entprellt, weil bei jedem Tastendruck zu schreiben (samt PNG-DataURLs der
   * Unterschriften) das Tippen spuerbar bremsen wuerde. Sie kann NIE etwas
   * kaputt machen: sie laeuft nur waehrend ungespeicherter Aenderungen, und
   * ein voller oder gesperrter Speicher wird abgefangen (siehe
   * einweisungEntwurf.ts).
   */
  useEffect(() => {
    if (!dirty || angebot || !darfEntwurfSichern(einweisung, isReadOnly)) return;
    const id = window.setTimeout(() => {
      setSicherung(sichereEntwurf(
        baueEntwurf(einweisung.id, einweisung.updated_at, aktuelleDaten(), new Date().toISOString()),
      ));
    }, 700);
    return () => window.clearTimeout(id);
    // Absichtlich die EINZELNEN Felder statt `einweisung` und `aktuelleDaten`:
    // beides wechselt bei jedem Zeichnen die Identitaet und wuerde den
    // Zeitgeber endlos neu aufziehen — gesichert wuerde dann nie.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dirty, angebot, isReadOnly, einweisung.id, einweisung.status, einweisung.updated_at,
    titel, datum, uhrzeit, ort, dauer, durchfuehrender, bemerkung, gruppen, teilnehmer, leerzeilen,
  ]);

  /**
   * Den angebotenen Stand uebernehmen.
   *
   * Fehlen im Entwurf die Unterschriften (er passte nur ohne sie in den
   * Speicher), werden die des Servers wieder eingesetzt — sonst waere die
   * Platzsparmassnahme ein Datenverlust.
   */
  const entwurfUebernehmen = () => {
    if (!angebot) return;
    // Das Angebot steht als Band ueber einem voll bedienbaren Editor. Wer erst
    // zehn Minuten tippt und dann „Uebernehmen" drueckt, verlaengt sonst
    // kommentarlos den alten Stand ueber die eigene Arbeit.
    if (dirty && !window.confirm(
      'Der gesicherte Stand ersetzt die Eingaben, die seit dem Öffnen entstanden sind. Übernehmen?',
    )) return;
    const d = angebot.daten;
    setTitel(d.titel || '');
    setDatum(d.datum || '');
    setUhrzeit(d.uhrzeit || '');
    setOrt(d.ort || '');
    setDauer(d.dauer || '');
    setDurchfuehrender(d.durchfuehrender || '');
    setBemerkung(d.bemerkung || '');
    setGruppen(d.punkte || []);
    setTeilnehmer(angebot.ohneUnterschriften
      ? verbindeUnterschriften(d.teilnehmer || [], einweisung.teilnehmer || [])
      : (d.teilnehmer || []));
    setLeerzeilen(d.leerzeilen ?? 5);
    setDirty(true);
    setAngebot(null);
    setHinweis('Der auf diesem Gerät gesicherte Stand wurde übernommen — bitte speichern.');
  };

  const entwurfVerwerfen = () => {
    raeumeEntwurf(einweisung.id);
    setAngebot(null);
  };

  /**
   * Zurueck zur Ablage — bei ungespeicherten Aenderungen nur nach Rueckfrage.
   *
   * WARUM: bis zum Speichern liegen Punkte UND Unterschriften nur im
   * Arbeitsspeicher. Der Zurueck-Pfeil sitzt oben links, genau dort, wo der
   * Daumen ohnehin haelt — ein Fehlgriff nach fuenfzehn Unterschriften darf
   * nicht kommentarlos alles verwerfen.
   *
   * Die Geraetesicherung ersetzt die Frage NICHT, sie entschaerft sie nur:
   * deshalb sagt der Text, ob es etwas gibt, das beim naechsten Oeffnen wieder
   * angeboten wird.
   */
  const zurueck = () => {
    if (dirty && !gesperrt) {
      const gesichert = !!sicherung && sicherung !== 'aus';
      const ok = window.confirm(
        'Es gibt ungespeicherte Änderungen.'
        + (gesichert
          ? ' Sie liegen auf diesem Gerät gesichert und werden beim nächsten Öffnen angeboten.'
          : ' Ohne Speichern gehen sie verloren.')
        + ' Trotzdem zurück?',
      );
      if (!ok) return;
    }
    onZurueck();
  };

  /** Geraet + online — was auf dem Blatt steht und Seiten kostet. */
  const teilnehmerGesamt = teilnehmer.length + online.length;

  const schaetzung = useMemo(
    () => schaetzeSeiten(gruppen, teilnehmerGesamt, leerzeilen),
    [gruppen, teilnehmerGesamt, leerzeilen],
  );

  /** Der aktuelle Bearbeitungsstand als Datensatz — fuer Vorschau und PDF. */
  const alsDatensatz = (): SigeEinweisung => ({
    ...einweisung,
    titel: titel.trim() || null,
    datum,
    uhrzeit: uhrzeit.trim() || null,
    ort: ort.trim() || null,
    dauer_min: dauer.trim() === '' ? null : Number(dauer.replace(/\D/g, '')) || null,
    durchfuehrender_name: durchfuehrender.trim() || null,
    bemerkung: bemerkung.trim() || null,
    punkte: bereinigeGruppen(gruppen),
    teilnehmer: bereinigeTeilnehmer(teilnehmer),
    leerzeilen,
  });

  /**
   * Derselbe Stand, aber mit der VEREINIGTEN Teilnehmerliste — fuer
   * Seitenzaehler, Vorschau und PDF. NIE zum Speichern verwenden.
   */
  const alsDatensatzMitOnline = (): SigeEinweisung => {
    const d = alsDatensatz();
    return { ...d, teilnehmer: vereinigeTeilnehmer(d, online) };
  };

  /**
   * Die genaue Seitenzahl aus dem PDF-Generator.
   *
   * Sie setzt das Dokument wirklich und ist deshalb zu teuer fuer jeden
   * Tastendruck — sie laeuft verzoegert, nachdem das Tippen aufgehoert hat.
   * Bis sie da ist (und wenn der Satz scheitert), steht die billige Schaetzung.
   */
  const [genaueSeiten, setGenaueSeiten] = useState<EinweisungSeiten | null>(null);

  useEffect(() => {
    setGenaueSeiten(null);
    const id = window.setTimeout(() => {
      setGenaueSeiten(zaehleSeitenSicher(alsDatensatzMitOnline(), company, { projektName, projektNummer, bauvorhabenAdresse }));
    }, 600);
    return () => window.clearTimeout(id);
    // Nur der Blattinhalt bestimmt die Seitenzahl — Kopfdaten wie Ort oder
    // Bemerkung stehen im Kopf-/Fussbereich und veraendern sie nicht spuerbar.
  }, [gruppen, teilnehmer, online, leerzeilen, company, projektName, projektNummer, bauvorhabenAdresse]);

  const anzeige = useMemo(
    () => (genaueSeiten ? beschreibeSeitenGenau(genaueSeiten) : beschreibeSeiten(schaetzung)),
    [genaueSeiten, schaetzung],
  );

  /** Zustandszeile: liegt der Stand auf dem Server, oder nur hier? */
  const speicherstand = useMemo(
    () => beschreibeSpeicherstand({
      dirty, laeuft: speichert, fehlversuche: autoFehler, zuletztGespeichert: zuletztAuto,
    }),
    [dirty, speichert, autoFehler, zuletztAuto],
  );

  /**
   * `automatisch` unterscheidet den Knopfdruck vom Speichern aus dem
   * Zeitgeber. Der Unterschied liegt allein in der Rueckmeldung: der
   * Knopfdruck darf eine Meldung setzen und einen Fehler gross anzeigen, das
   * automatische Speichern meldet sich nur in der Zustandszeile — sonst
   * springt dem Anwender alle paar Sekunden beim Tippen ein Kasten ins Bild.
   */
  const speichern = async (
    opt?: { automatisch?: boolean },
  ): Promise<SigeEinweisung | null> => {
    const automatisch = opt?.automatisch === true;
    if (gesperrt || speichertRef.current) return null;
    speichertRef.current = true;
    setSpeichert(true);
    if (!automatisch) setFehler(null);
    try {
      const entwurf = alsDatensatz();
      const gespeichert = await updateEinweisung(einweisung.id, {
        titel: entwurf.titel,
        datum: entwurf.datum,
        uhrzeit: entwurf.uhrzeit,
        ort: entwurf.ort,
        dauer_min: entwurf.dauer_min,
        durchfuehrender_name: entwurf.durchfuehrender_name,
        bemerkung: entwurf.bemerkung,
        punkte: entwurf.punkte,
        teilnehmer: entwurf.teilnehmer,
        leerzeilen: entwurf.leerzeilen,
      });
      setDirty(false);
      // Der Server hat den Stand — die Sicherung auf dem Geraet hat ihren
      // Zweck erfuellt und wird geraeumt. Bliebe sie stehen, faende der
      // naechste Aufruf einen „ungesicherten Stand", den es nicht mehr gibt.
      raeumeEntwurf(einweisung.id);
      setSicherung(null);
      // Und das unbeantwortete Angebot verfaellt mit. Ohne diese Zeile bleibt
      // das Band stehen, obwohl der lokale Stand eben geloescht wurde: ein
      // Tipp auf „Uebernehmen" spielte danach den ALTEN Stand aus dem
      // Komponentenspeicher zurueck, und das naechste Speichern schriebe ihn
      // ueber den gerade gesicherten — auf der Baustelle waeren das frisch
      // geleistete Unterschriften.
      setAngebot(null);
      setAutoFehler(0);
      if (automatisch) {
        setZuletztAuto(uhrzeitJetzt());
        // Kein `setHinweis`: die Zustandszeile sagt es leise, eine Meldung
        // ueber dem Blatt waere alle paar Sekunden nur im Weg.
      } else {
        setZuletztAuto(null);
        setHinweis('Gespeichert.');
      }
      onGespeichert(gespeichert);
      return gespeichert;
    } catch (e) {
      // Der automatische Anlauf zaehlt hoch statt zu schreien: der Zaehler
      // zieht den naechsten Versuch auf (autoSpeicherPlan) und faerbt die
      // Zustandszeile. Ungespeichert bleibt der Stand in beiden Faellen —
      // dass er nicht auf dem Server liegt, MUSS sichtbar sein.
      if (automatisch) setAutoFehler((n) => n + 1);
      else setFehler(describeError(e));
      return null;
    } finally {
      speichertRef.current = false;
      setSpeichert(false);
    }
  };

  /** Uhrzeit fuer die Zustandszeile — nur Stunde und Minute, ohne Datum. */
  const uhrzeitJetzt = (): string =>
    new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

  /**
   * AUTOMATISCHES SPEICHERN.
   *
   * Der Speichern-Knopf bleibt — aber auf der Baustelle drueckt ihn niemand
   * zuverlaessig: dort wird unterschrieben, weitergegangen und das Telefon
   * eingesteckt. Die Geraetesicherung daneben faengt nur DIESES Geraet auf;
   * erst der Server macht den Stand fuer alle sichtbar und ueberlebt ein
   * verlorenes Telefon.
   *
   * Ob und wann geschrieben wird, entscheidet `autoSpeicherPlan` (pur und
   * getestet): nicht in der Nur-Ansicht, nicht am abgeschlossenen Blatt, nicht
   * waehrend ein anderer Schreibvorgang laeuft, und vor allem NICHT, solange
   * die Frage nach dem Geraetestand offensteht — sonst schriebe der Zeitgeber
   * die Entscheidung fest, bevor der Anwender sie getroffen hat.
   */
  useEffect(() => {
    const plan = autoSpeicherPlan({
      dirty,
      gesperrt,
      angebotOffen: !!angebot,
      laeuft: speichert || schliesstAb,
      fehlversuche: autoFehler,
    });
    if (!plan.speichern) return;
    const id = window.setTimeout(() => {
      // Zwischen `schliesstAbRef = true` und dem naechsten Zeichnen liegt ein
      // Fenster, in dem der Zeitgeber noch feuern kann. Der Ref wirkt sofort
      // und haelt es zu — sonst schoebe sich ein automatisches Speichern
      // zwischen Rueckfrage und Abschluss.
      if (schliesstAbRef.current) return;
      void speichern({ automatisch: true });
    }, plan.wartezeit);
    return () => window.clearTimeout(id);
    // Wie bei der Geraetesicherung die EINZELNEN Felder: jede Eingabe zieht den
    // Zeitgeber neu auf, geschrieben wird erst in der Tipp-Pause.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dirty, gesperrt, angebot, speichert, schliesstAb, autoFehler,
    titel, datum, uhrzeit, ort, dauer, durchfuehrender, bemerkung, gruppen, teilnehmer, leerzeilen,
  ]);

  /**
   * Jede neue Eingabe ist ein neuer Anlauf wert: der Fehlversuchszaehler faellt
   * zurueck, damit nach einem Funkloch nicht bis zum naechsten Blattwechsel
   * geschwiegen wird. Bewusst an EINER Stelle statt an den sieben Settern.
   */
  useEffect(() => {
    setAutoFehler(0);
  }, [titel, datum, uhrzeit, ort, dauer, durchfuehrender, bemerkung, gruppen, teilnehmer, leerzeilen]);

  /**
   * Abschliessen: erst SPEICHERN, dann einfrieren.
   *
   * Die Reihenfolge ist tragend — nach `abschliessenEinweisung` nimmt der
   * Entwurfs-Guard in updateEinweisung keine Aenderung mehr an. Wer die
   * letzte Unterschrift eintraegt und direkt auf „Abschließen" drueckt,
   * verloere sie sonst.
   */
  /**
   * Wartet, bis ein laufendes Speichern durch ist.
   *
   * Seit das Blatt sich alle paar Sekunden selbst speichert, faellt der
   * Abschliessen-Knopf sonst regelmaessig auf ein laufendes Schreiben — und
   * frueher hiess das: der Knopf tut wortlos NICHTS, das Blatt bleibt offen
   * und die Abrechnungsfrage kommt nie. Warten ist hier richtig, abbrechen
   * war es nie.
   */
  const warteAufSchreibruhe = async (maxMs = 8000): Promise<boolean> => {
    const bis = Date.now() + maxMs;
    while (speichertRef.current) {
      if (Date.now() > bis) return false;
      await new Promise((r) => window.setTimeout(r, 100));
    }
    return true;
  };

  const abschliessen = async () => {
    // Der Riegel steht VOR der Rueckfrage: sonst stuenden nach zwei
    // Beruehrungen zwei Dialoge uebereinander, und beide Ja wuerden laufen.
    // `speichertRef` gehoert hier ausdruecklich NICHT hin — ein gerade
    // laufendes automatisches Speichern darf den Abschluss verzoegern, nicht
    // verschlucken.
    if (gesperrt || schliesstAbRef.current) return;
    if (!window.confirm(
      'Die Einweisung wird abgeschlossen und ist danach schreibgeschützt. '
      + 'Danach kann der Link für die Online-Einweisung aktiviert werden. Fortfahren?',
    )) return;

    schliesstAbRef.current = true;
    setSchliesstAb(true);
    setFehler(null);
    try {
      if (!(await warteAufSchreibruhe())) {
        setFehler('Das Speichern dauert ungewöhnlich lange. Bitte kurz warten und erneut abschließen.');
        return;
      }
      const gespeichert = await speichern();
      if (!gespeichert) {
        // Ohne diese Meldung bricht das Abschliessen STILL ab: der Anwender
        // sieht ein offenes Blatt, keine Erklaerung und keine Abrechnung.
        setFehler((vorher) => vorher
          || 'Die Einweisung konnte nicht gespeichert werden und wurde deshalb nicht abgeschlossen.');
        return;
      }
      const fertig = await abschliessenEinweisung(einweisung.id);
      raeumeEntwurf(einweisung.id);
      onGespeichert(fertig);
      onAbgeschlossen(fertig);
    } catch (e) {
      setFehler(describeError(e));
    } finally {
      schliesstAbRef.current = false;
      setSchliesstAb(false);
    }
  };

  const holeVorschlag = async () => {
    if (gesperrt) return;
    setVorschlagLaeuft(true);
    setFehler(null);
    try {
      const quelle = await ladePlanQuelle(einweisung.project_id);
      const vorschlag = buildEinweisungVorschlag(
        quelle.sigeProject,
        quelle.arbeitsschritte,
        quelle.gefaehrdungen,
        quelle.massnahmen,
        quelle.bloecke,
      );
      setGruppen((prev) => uebernimmVorschlag(prev, vorschlag));
      setDirty(true);
      setHinweis(quelle.sigeProject
        ? 'Vorschlag ergänzt — vorhandene Punkte blieben stehen.'
        : 'Für dieses Projekt gibt es noch keinen SiGe-Plan. Ergänzt wurden die fachlichen Standardpunkte.');
    } catch (e) {
      setFehler(describeError(e));
    } finally {
      setVorschlagLaeuft(false);
    }
  };

  /**
   * PDF herunterladen. BUCHT NICHT — gebucht wird ausschliesslich beim
   * Abschliessen (siehe einweisungIntegration.ts). Deshalb steht der Knopf
   * auch im Lesemodus und bei abgeschlossenen Blaettern offen.
   */
  const exportieren = () => {
    if (exportiert) return;
    setFehler(null);
    setExportiert(true);
    // Der Satz laeuft im selben Faden wie die Oberflaeche und dauert auf einem
    // Handy spuerbar. Ohne diese Verzoegerung wuerde der Wartezustand nie
    // gezeichnet — der Knopf saehe aus, als haette er nicht reagiert.
    window.setTimeout(() => {
      try {
        const ergebnis = exportiereEinweisungPdf(alsDatensatzMitOnline(), company, {
          projektName,
          projektNummer,
          bauvorhabenAdresse,
        });
        if (ergebnis.status === 'fehler') setFehler(ergebnis.fehler);
        else setGenaueSeiten(ergebnis.wert);
      } finally {
        setExportiert(false);
      }
    }, 30);
  };

  /** „+ Punkt" der Mobilleiste: haengt an die letzte Gruppe an, notfalls an eine neue. */
  const punktAnhaengen = () => {
    if (gesperrt) return;
    setGruppen((prev) => {
      if (prev.length === 0) {
        const id = `eigen-${crypto.randomUUID()}`;
        return fuegePunktHinzu(fuegeGruppeHinzu(prev, id, 'Weitere Hinweise'), id);
      }
      return fuegePunktHinzu(prev, prev[prev.length - 1].id);
    });
    setDirty(true);
  };

  const feld = `w-full px-3 py-2 ${isMobile ? 'min-h-[44px]' : ''} text-sm text-slate-800 bg-slate-50 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-blue-300 disabled:text-slate-500`;

  return (
    <div data-glass="seite" className="flex flex-col h-full bg-slate-50">
      {/* Kopfleiste */}
      <div data-glass="kopf" className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 shrink-0">
        <button onClick={zurueck} className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-slate-100 rounded-full transition-colors">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-bold text-slate-800 truncate">{einweisungTitel(einweisung)}</h1>
          <p className="text-xs text-slate-500 truncate">
            {formatDatum(datum)}{ort ? ` · ${ort}` : ''} · {teilnehmerGesamt} Teilnehmer
            {online.length > 0 ? ` (${online.length} online)` : ''}
          </p>
        </div>
        {abgeschlossen && (
          <span className="shrink-0 flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full">
            <Lock className="w-3 h-3" /> Abgeschlossen
          </span>
        )}
      </div>

      {/* Gefundener Stand auf diesem Geraet — wird ANGEBOTEN, nie eingespielt. */}
      {angebot && (
        <div className="px-4 pt-3 shrink-0">
          <div className="p-3 bg-amber-50 border border-amber-300 rounded-xl space-y-2">
            <p className="text-xs text-amber-900 flex items-start gap-2">
              <HardDriveDownload className="w-4 h-4 shrink-0 mt-px" />
              <span className="flex-1">{entwurfsFrage(angebot)}</span>
            </p>
            <div className="flex gap-2">
              <button
                onClick={entwurfUebernehmen}
                className="flex-1 min-h-[44px] px-4 flex items-center justify-center gap-2 text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-xl transition-colors"
              >
                Übernehmen
              </button>
              <button
                onClick={entwurfVerwerfen}
                className="flex-1 min-h-[44px] px-4 flex items-center justify-center gap-2 text-sm font-semibold text-amber-900 bg-white border border-amber-300 hover:bg-amber-100 rounded-xl transition-colors"
              >
                Verwerfen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Meldungen */}
      {(fehler || hinweis || isReadOnly) && (
        <div className="px-4 pt-3 space-y-2 shrink-0">
          {isReadOnly && (
            <div className="p-3 bg-amber-50 border border-amber-300 rounded-xl text-xs text-amber-900">
              Nur-Ansicht: Dieses Projekt ist schreibgeschützt geöffnet. Es wird nichts gespeichert,
              abgeschlossen oder gebucht.
            </div>
          )}
          {fehler && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-px" /> <span className="flex-1">{fehler}</span>
              <button onClick={() => setFehler(null)} className="p-1"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}
          {hinweis && !fehler && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-800 flex items-start gap-2">
              <span className="flex-1">{hinweis}</span>
              <button onClick={() => setHinweis(null)} className="p-1"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}
        </div>
      )}

      {/* Der Inhalt scrollt in seinem eigenen Bereich; das Polster unten haelt
          die letzte Zeile von der festen Leiste frei. */}
      <div className={`flex-1 overflow-y-auto p-4 space-y-5 ${isMobile ? 'pb-10' : 'pb-8'}`}>
        {/* Kopfdaten — auf dem Handy eingeklappt, dort zaehlt der Punkte-Editor. */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <button
            onClick={() => setKopfOffen(!kopfOffen)}
            className="w-full px-4 py-3 min-h-[44px] flex items-center justify-between text-left bg-slate-50/60 hover:bg-slate-100/60 transition-colors"
          >
            <span className="text-sm font-bold text-slate-800">Kopfdaten</span>
            {kopfOffen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </button>
          {kopfOffen && (
            <div className={`p-4 grid gap-3 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
              <label className="block">
                <span className="block text-xs font-semibold text-slate-500 mb-1">Datum</span>
                <input type="date" value={datum} onChange={(e) => aendern(setDatum)(e.target.value)} disabled={gesperrt} className={feld} />
              </label>
              <label className="block">
                <span className="block text-xs font-semibold text-slate-500 mb-1">Uhrzeit</span>
                <input type="time" value={uhrzeit} onChange={(e) => aendern(setUhrzeit)(e.target.value)} disabled={gesperrt} className={feld} />
              </label>
              <label className="block">
                <span className="block text-xs font-semibold text-slate-500 mb-1">Ort</span>
                <input value={ort} onChange={(e) => aendern(setOrt)(e.target.value)} disabled={gesperrt} placeholder="z. B. Baustellenbüro" className={feld} />
              </label>
              <label className="block">
                <span className="block text-xs font-semibold text-slate-500 mb-1">Dauer (Minuten)</span>
                <input
                  value={dauer}
                  onChange={(e) => aendern(setDauer)(e.target.value.replace(/\D/g, '').slice(0, 3))}
                  disabled={gesperrt}
                  inputMode="numeric"
                  placeholder="30"
                  className={feld}
                />
              </label>
              <label className="block">
                <span className="block text-xs font-semibold text-slate-500 mb-1">Durchführender</span>
                <input value={durchfuehrender} onChange={(e) => aendern(setDurchfuehrender)(e.target.value)} disabled={gesperrt} className={feld} />
              </label>
              <label className="block">
                <span className="block text-xs font-semibold text-slate-500 mb-1">Bezeichnung (optional)</span>
                <input value={titel} onChange={(e) => aendern(setTitel)(e.target.value)} disabled={gesperrt} placeholder={`Einweisung Nr. ${einweisung.nummer}`} className={feld} />
              </label>
              <label className={`block ${isMobile ? '' : 'col-span-2'}`}>
                <span className="block text-xs font-semibold text-slate-500 mb-1">Bemerkung</span>
                <textarea value={bemerkung} onChange={(e) => aendern(setBemerkung)(e.target.value)} disabled={gesperrt} rows={2} className={feld} />
              </label>
              {(einweisung.plan_nr || einweisung.plan_stand) && (
                <p className="col-span-full text-xs text-slate-400">
                  Planbezug: {einweisung.plan_nr ? `Nr. ${einweisung.plan_nr}` : 'ohne Nummer'}
                  {einweisung.plan_stand ? `, Stand ${einweisung.plan_stand}` : ''}
                </p>
              )}
            </div>
          )}
        </div>

        <PunkteEditor
          gruppen={gruppen}
          onChange={(g) => { setGruppen(g); setDirty(true); }}
          anzeige={anzeige}
          onVorschlag={holeVorschlag}
          vorschlagLaeuft={vorschlagLaeuft}
          isReadOnly={gesperrt}
          isMobile={isMobile}
        />

        {/* ─── Online-Einweisung per QR ─────────────────────────────────
            Im Entwurf nur ein Hinweis: der Link geht erst auf ein
            eingefrorenes Blatt (Plan §1.2). Abgeschlossen: die online
            eingetragenen Teilnehmer, schreibgeschuetzt — sie liegen in
            ihrer eigenen Tabelle und stehen im PDF hinter den Zeilen vom
            Geraet. */}
        {abgeschlossen ? (
          <OnlineBlock online={online} isMobile={isMobile} onAlle={onOnlineTeilnahmen} />
        ) : (
          <div className="p-3 bg-teal-50 border border-teal-200 rounded-xl text-xs text-teal-900 flex items-start gap-2">
            <Globe className="w-4 h-4 shrink-0 mt-px" />
            <span>
              Online-Einweisung per QR: nach dem Abschließen den Link in der Ablage aktivieren.
              Teilnehmer lesen die Punkte dann auf dem eigenen Handy und unterschreiben dort.
            </span>
          </div>
        )}

        <TeilnehmerListe
          teilnehmer={teilnehmer}
          onChange={(t) => { setTeilnehmer(t); setDirty(true); }}
          leerzeilen={leerzeilen}
          onLeerzeilenChange={(n) => { setLeerzeilen(n); setDirty(true); }}
          isReadOnly={gesperrt}
          isMobile={isMobile}
        />

        {/* Aktionen — auf dem Desktop hier, auf dem Handy zusaetzlich unten fest. */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setZeigeVorschau(true)}
            className="px-4 py-2.5 min-h-[44px] flex items-center gap-2 text-sm font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl transition-colors"
          >
            <Eye className="w-4 h-4" /> Vorschau
          </button>
          <button
            onClick={exportieren}
            disabled={exportiert}
            className="px-4 py-2.5 min-h-[44px] flex items-center gap-2 text-sm font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl transition-colors disabled:opacity-50"
          >
            {exportiert ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
            {exportiert ? 'Erzeugt…' : 'PDF'}
          </button>
          {/* Am Desktop stand bisher gar kein Zustand — mit dem automatischen
              Speichern ist er Pflicht: sonst weiss niemand, ob der Stand schon
              auf dem Server liegt oder nur im Fenster steht. */}
          {!gesperrt && (
            <span className={`self-center text-xs ${TON_KLASSE[speicherstand.ton]}`}>
              {speicherstand.text}
            </span>
          )}
          {!gesperrt && (
            <>
              <button
                onClick={() => void speichern()}
                disabled={speichert}
                className="px-4 py-2.5 min-h-[44px] flex items-center gap-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors disabled:opacity-50"
              >
                {speichert ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {speichert ? 'Speichert…' : 'Speichern'}
              </button>
              <button
                data-tutorial="einweisung.abschliessen"
                onClick={() => void abschliessen()}
                disabled={schliesstAb || speichert}
                className="px-4 py-2.5 min-h-[44px] flex items-center gap-2 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition-colors disabled:opacity-50"
              >
                {schliesstAb ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {schliesstAb ? 'Schließt ab…' : 'Abschließen'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ═══ Feste Aktionsleiste auf dem Handy ═══════════════════════════════
          Die beiden Handgriffe, die auf der Baustelle laufend gebraucht werden,
          plus der Zustand des Blattes. Sie liegt als letztes Kind im
          Spalten-Flex und verdeckt deshalb nichts — der Inhalt darueber
          scrollt in seinem eigenen Bereich. Das Polster unten faengt den
          Home-Indicator ab. Auf dem Desktop erscheint sie nicht; dort stehen
          dieselben Knoepfe im Fluss unter der Teilnehmerliste. */}
      {isMobile && !gesperrt && (
        <div
          className="shrink-0 bg-white border-t border-slate-200 px-3 py-2 space-y-1.5"
          style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}
        >
          <div className="flex items-center justify-between gap-2 px-0.5 text-[11px]">
            <span className={TON_KLASSE[speicherstand.ton]}>{speicherstand.text}</span>
            {/* Der Stand der Gerätesicherung — nur, solange es etwas zu
                sichern gibt, und ohne Alarm: ohne sie laeuft alles weiter. */}
            {dirty && sicherung && (
              <span className={sicherung === 'gesichert' ? 'text-slate-400' : 'text-amber-700 font-semibold'}>
                {beschreibeSicherung(sicherung)}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={punktAnhaengen}
              className="flex-1 min-h-[48px] flex items-center justify-center gap-2 text-sm font-bold text-blue-700 bg-blue-50 rounded-xl"
            >
              <Plus className="w-4 h-4" /> Punkt
            </button>
            <button
              onClick={() => void speichern()}
              disabled={speichert || schliesstAb}
              className={`flex-1 min-h-[48px] flex items-center justify-center gap-2 text-sm font-bold text-white rounded-xl disabled:opacity-50 ${dirty ? 'bg-blue-600' : 'bg-slate-400'}`}
            >
              {speichert ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {speichert ? 'Speichert…' : 'Speichern'}
            </button>
          </div>
        </div>
      )}

      {zeigeVorschau && (
        <EinweisungVorschau
          einweisung={alsDatensatzMitOnline()}
          projektName={projektName}
          projektNummer={projektNummer}
          onClose={() => setZeigeVorschau(false)}
        />
      )}
    </div>
  );
};

/**
 * „Online eingetragen (n)" — die Zeilen aus der Teilnahmen-Tabelle, nur
 * Anzeige. Kein Loeschen, kein Bearbeiten: eine Online-Teilnahme ist der
 * Nachweis, dass die Person eingewiesen wurde (Betreiber 12.09.).
 */
const OnlineBlock: React.FC<{
  online: EinweisungOnlineTeilnahme[];
  isMobile: boolean;
  onAlle?: () => void;
}> = ({ online, isMobile, onAlle }) => {
  const zeilen = sortiereOnlineTeilnahmen(online);
  return (
    <div className="bg-white border border-teal-200 rounded-2xl overflow-hidden">
      <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-2 bg-teal-50/60">
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
          <Globe className="w-4 h-4 text-teal-600" /> Online eingetragen ({online.length})
        </h3>
        {onAlle && (
          <button
            type="button"
            onClick={onAlle}
            className="min-h-[36px] px-3 flex items-center gap-1.5 text-xs font-semibold text-teal-700 bg-white border border-teal-200 hover:bg-teal-50 rounded-lg transition-colors"
          >
            <Users className="w-3.5 h-3.5" /> Teilnahmen-Ansicht
          </button>
        )}
      </div>
      {zeilen.length === 0 ? (
        <p className="px-4 py-3 text-xs text-slate-500">
          Noch niemand online eingetragen. Der Link wird in der Ablage über „Link" aktiviert;
          dort gibt es auch QR-Code und Aushang.
        </p>
      ) : (
        <div className="divide-y divide-slate-100">
          {zeilen.map((t) => (
            <div key={t.id} className={`px-4 py-2 flex gap-3 ${isMobile ? 'items-start' : 'items-center'}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-800 truncate">
                  <span className="font-semibold">{onlineTeilnehmerName(t)}</span>
                  {(t.funktion || '').trim() ? <span className="text-slate-500">, {t.funktion!.trim()}</span> : null}
                </p>
                <p className="text-xs text-slate-500 truncate">
                  {t.firma} · {formatZeitpunkt(t.unterschrieben_am)}
                </p>
              </div>
              <img
                src={t.unterschrift}
                alt={`Unterschrift ${onlineTeilnehmerName(t)}`}
                className="max-h-10 w-24 object-contain bg-white border border-slate-200 rounded shrink-0"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Vorschau des Blattes — bewusst als HTML und nicht ueber den PDF-Generator.
 *
 * Sie soll auch dann etwas zeigen, wenn der Generator nicht erreichbar ist,
 * und sie darf auf der Baustelle nichts nachladen. Verbindlich ist trotzdem
 * das PDF; das steht auch so darueber.
 */
const EinweisungVorschau: React.FC<{
  einweisung: SigeEinweisung;
  projektName?: string;
  projektNummer?: string;
  onClose: () => void;
}> = ({ einweisung, projektName, projektNummer, onClose }) => (
  <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-3" onClick={onClose}>
    <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between shrink-0">
        <h2 className="text-sm font-bold text-slate-800">Vorschau — verbindlich ist das PDF</h2>
        <button onClick={onClose} className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-slate-100 rounded-full">
          <X className="w-4 h-4 text-slate-500" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-6 space-y-5 text-slate-800">
        <div>
          <h1 className="text-lg font-bold">Einweisung in den SiGe-Plan</h1>
          <p className="text-sm text-slate-600">
            {projektName || '—'}{projektNummer ? ` (${projektNummer})` : ''}
          </p>
          <p className="text-sm text-slate-600">
            Nr. {einweisung.nummer} · {formatDatum(einweisung.datum)}
            {einweisung.uhrzeit ? `, ${einweisung.uhrzeit} Uhr` : ''}
            {einweisung.ort ? ` · ${einweisung.ort}` : ''}
            {einweisung.dauer_min ? ` · ${einweisung.dauer_min} Min.` : ''}
          </p>
          {einweisung.durchfuehrender_name && (
            <p className="text-sm text-slate-600">Durchführender: {einweisung.durchfuehrender_name}</p>
          )}
        </div>

        {einweisung.punkte.map((g) => (
          <div key={g.id}>
            <h3 className="text-sm font-bold border-b border-slate-200 pb-1 mb-1">{g.titel}</h3>
            <ul className="list-disc pl-5 space-y-0.5">
              {g.punkte.map((p, i) => <li key={i} className="text-sm">{p}</li>)}
            </ul>
          </div>
        ))}

        <div>
          <h3 className="text-sm font-bold border-b border-slate-200 pb-1 mb-2">
            Unterschriften ({einweisung.teilnehmer.length} Teilnehmer, {zaehleUnterschriften(einweisung.teilnehmer)} unterschrieben)
          </h3>
          <table className="w-full text-sm">
            <tbody>
              {einweisung.teilnehmer.map((t) => (
                <tr key={t.id} className="border-b border-slate-100">
                  <td className="py-2 w-1/3">
                    {t.name}
                    {t.funktion ? <span className="text-slate-500">, {t.funktion}</span> : null}
                    {t.quelle === 'online' && (
                      <span className="ml-1.5 px-1.5 py-px text-[10px] font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded-full align-middle">
                        online
                      </span>
                    )}
                  </td>
                  <td className="py-2 w-1/3 text-slate-500">{t.firma || ''}</td>
                  <td className="py-2 w-1/3">
                    {t.unterschrift
                      ? <img src={t.unterschrift} alt="" className="h-8 object-contain" />
                      : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
              {Array.from({ length: einweisung.leerzeilen }).map((_, i) => (
                <tr key={`blanko-${i}`} className="border-b border-slate-100">
                  <td className="py-4 text-slate-300">—</td><td /><td />
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {einweisung.bemerkung && <p className="text-sm text-slate-600">Bemerkung: {einweisung.bemerkung}</p>}
      </div>
    </div>
  </div>
);
